import { ArrowLeft, Mic, RefreshCcw, Square, UploadCloud, Video } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { mediaRecordingService, type RecordingUploadResult, type RecordingUploadTarget } from '../../../shared/services';
import { useShop } from '../../../shared/store';

type RecorderMode = 'audio' | 'video' | 'animation';
type RecorderStatus = 'idle' | 'requesting' | 'recording' | 'ready' | 'uploading' | 'success' | 'error';

const recorderModes: Record<
  RecorderMode,
  {
    label: string;
    endpoint: RecordingUploadTarget;
    sourceType: 'microphone' | 'camera';
    accept: string;
    constraints: MediaStreamConstraints;
    icon: typeof Mic;
  }
> = {
  audio: {
    label: 'Audio',
    endpoint: 'audio',
    sourceType: 'microphone',
    accept: 'audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm',
    constraints: { audio: true, video: false },
    icon: Mic,
  },
  video: {
    label: 'Video',
    endpoint: 'video',
    sourceType: 'camera',
    accept: 'video/*,.mp4,.webm,.ogg,.mov',
    constraints: { audio: true, video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } } },
    icon: Video,
  },
  animation: {
    label: 'Animation',
    endpoint: 'animation',
    sourceType: 'camera',
    accept: 'video/*,.mp4,.webm,.ogg,.mov',
    constraints: { audio: true, video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } } },
    icon: UploadCloud,
  },
};

const buildDefaultName = (mode: RecorderMode) => `${mode}-recording-${new Date().toISOString().replace(/[:.]/g, '-')}`;
const sanitizeFileBaseName = (value: string, fallback: string) =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || fallback;

const formatBytes = (value?: number) => {
  if (!value) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const size = value / 1024 ** index;
  return `${size.toFixed(size >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
};

const pickMimeType = (mode: RecorderMode) => {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return undefined;
  const candidates =
    mode === 'audio'
      ? ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
      : ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate));
};

const extensionFromMimeType = (mimeType: string, mode: RecorderMode) => {
  if (mimeType.includes('mp4')) return 'mp4';
  if (mimeType.includes('ogg')) return 'ogg';
  return mode === 'audio' ? 'webm' : 'webm';
};

const VendorAddProductPage = () => {
  const { currentUser } = useShop();
  const [mode, setMode] = useState<RecorderMode>('video');
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [feedback, setFeedback] = useState('Choose a media type and start recording inside the app.');
  const [recordingName, setRecordingName] = useState(buildDefaultName('video'));
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [recordedFile, setRecordedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<RecordingUploadResult | null>(null);

  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const rafRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);

  const config = recorderModes[mode];
  const ActiveIcon = config.icon;
  const supportsRecording = typeof window !== 'undefined' && !!window.navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';

  const drawIdleBars = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#fff7ed';
    context.fillRect(0, 0, canvas.width, canvas.height);
    for (let index = 0; index < 18; index += 1) {
      const height = 16 + ((Math.sin(index * 0.8) + 1) / 2) * 70;
      context.fillStyle = index % 2 === 0 ? '#fb923c' : '#fdba74';
      context.fillRect(index * 22, canvas.height - height, 14, height);
    }
  };

  const stopVisualizer = () => {
    if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    analyserRef.current?.disconnect();
    analyserRef.current = null;
    if (audioContextRef.current) void audioContextRef.current.close();
    audioContextRef.current = null;
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (liveVideoRef.current) liveVideoRef.current.srcObject = null;
  };

  const startVisualizer = (stream: MediaStream) => {
    if (!stream.getAudioTracks().length || typeof window.AudioContext === 'undefined') {
      drawIdleBars();
      return;
    }
    stopVisualizer();
    const audioContext = new window.AudioContext();
    const analyser = audioContext.createAnalyser();
    audioContext.createMediaStreamSource(stream).connect(analyser);
    analyser.fftSize = 64;
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    const data = new Uint8Array(analyser.frequencyBinCount);
    const draw = () => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext('2d');
      if (!canvas || !context || !analyserRef.current) return;
      analyserRef.current.getByteFrequencyData(data);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = '#fff7ed';
      context.fillRect(0, 0, canvas.width, canvas.height);
      data.forEach((value, index) => {
        const height = Math.max(14, (value / 255) * (canvas.height - 10));
        context.fillStyle = value > 170 ? '#ea580c' : value > 90 ? '#fb923c' : '#fdba74';
        context.fillRect(index * 20, canvas.height - height, 14, height);
      });
      rafRef.current = window.requestAnimationFrame(draw);
    };
    draw();
  };

  const resetTake = (message = 'Choose a media type and start recording inside the app.') => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setRecordedFile(null);
    setUploadResult(null);
    setStatus('idle');
    setFeedback(message);
    drawIdleBars();
  };

  const handleModeChange = (nextMode: RecorderMode) => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    stopStream();
    stopVisualizer();
    setMode(nextMode);
    setRecordingName(buildDefaultName(nextMode));
    resetTake(`Ready to capture ${nextMode}.`);
  };

  const handlePickedFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setRecordedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setRecordingName(file.name.replace(/\.[^.]+$/, '') || buildDefaultName(mode));
    setUploadResult(null);
    setStatus('ready');
    setFeedback(`${config.label} file selected. Add a name and upload when ready.`);
    drawIdleBars();
  };

  const handleStartRecording = async () => {
    if (!supportsRecording) {
      setStatus('error');
      setFeedback('This browser does not support MediaRecorder capture.');
      return;
    }
    try {
      resetTake(`Requesting ${config.sourceType === 'camera' ? 'camera and microphone' : 'microphone'} access...`);
      setStatus('requesting');
      const stream = await window.navigator.mediaDevices.getUserMedia(config.constraints);
      streamRef.current = stream;
      if (liveVideoRef.current && mode !== 'audio') {
        liveVideoRef.current.srcObject = stream;
        await liveVideoRef.current.play().catch(() => undefined);
      }
      const mimeType = pickMimeType(mode);
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const finalMime = recorder.mimeType || mimeType || (mode === 'audio' ? 'audio/webm' : 'video/webm');
        const file = new File(
          [new Blob(chunksRef.current, { type: finalMime })],
          `${sanitizeFileBaseName(recordingName, `${mode}-recording`)}.${extensionFromMimeType(finalMime, mode)}`,
          { type: finalMime },
        );
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setRecordedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setUploadResult(null);
        setStatus('ready');
        setFeedback(`${config.label} recording captured. Add a name if needed, then upload.`);
        stopStream();
        stopVisualizer();
        drawIdleBars();
      };
      recorder.start(250);
      startVisualizer(stream);
      setStatus('recording');
      setFeedback(`${config.label} recording started inside the app.`);
    } catch (error: unknown) {
      stopStream();
      stopVisualizer();
      setStatus('error');
      setFeedback(error instanceof Error ? error.message : 'Unable to start recording.');
      drawIdleBars();
    }
  };

  const handleUpload = async () => {
    if (!recordedFile) {
      setStatus('error');
      setFeedback('Record or choose a file first.');
      return;
    }
    const extension = recordedFile.name.split('.').pop() ?? extensionFromMimeType(recordedFile.type, mode);
    const fileToUpload = new File([recordedFile], `${sanitizeFileBaseName(recordingName, `${mode}-recording`)}.${extension}`, {
      type: recordedFile.type || undefined,
    });
    try {
      setStatus('uploading');
      setFeedback(`Uploading to /media/recordings/${config.endpoint}...`);
      const result = await mediaRecordingService.uploadRecording({
        endpoint: config.endpoint,
        file: fileToUpload,
        sourceType: config.sourceType,
        fallbackUserId: currentUser?.id,
      });
      setRecordedFile(fileToUpload);
      setUploadResult(result);
      setStatus('success');
      setFeedback(`${config.label} uploaded successfully.`);
    } catch (error: unknown) {
      setStatus('error');
      setFeedback(error instanceof Error ? error.message : 'Upload failed.');
    }
  };

  useEffect(() => {
    drawIdleBars();
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      stopStream();
      stopVisualizer();
    };
  }, [previewUrl]);

  const endpointList = [
    'GET /audio',
    'GET /audio/{id}',
    'GET /books/{id}',
    'POST /media/recordings/signature',
    'POST /media/recordings/audio',
    'POST /media/recordings/video',
    'POST /media/recordings/animation',
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-700">Media Studio</p>
            <h1 className="mt-1 text-3xl font-semibold text-slate-900">Record and upload inside the app</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">Click a mode, allow camera or microphone access, record in-app, then name and upload the result.</p>
          </div>
          <Link to="/vendor/dashboard/overview" className="inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-50">
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </Link>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_320px]">
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              {(Object.entries(recorderModes) as Array<[RecorderMode, (typeof recorderModes)[RecorderMode]]>).map(([entryMode, entry]) => {
                const Icon = entry.icon;
                const isActive = entryMode === mode;
                return (
                  <button key={entryMode} type="button" onClick={() => handleModeChange(entryMode)} className={`rounded-3xl border p-5 text-left ${isActive ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-white'}`}>
                    <div className={`inline-flex rounded-2xl p-3 ${isActive ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700'}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h2 className="mt-4 text-lg font-semibold text-slate-900">{entry.label}</h2>
                    <p className="mt-1 text-sm text-slate-600">Uploads to `/media/recordings/{entry.endpoint}`</p>
                  </button>
                );
              })}
            </div>

            <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{config.label} recorder</h2>
                  <p className="mt-2 text-sm text-slate-600">{feedback}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${status === 'recording' ? 'bg-emerald-100 text-emerald-700' : status === 'error' ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-700'}`}>{status}</span>
              </div>

              <div className="mt-5 overflow-hidden rounded-3xl border border-dashed border-orange-200 bg-slate-950">
                {mode === 'audio' ? (
                  <div className="grid min-h-[260px] place-items-center px-6 py-10 text-center text-white">
                    <div>
                      <Mic className="mx-auto h-12 w-12 text-orange-300" />
                      <p className="mt-4 text-base font-semibold">Microphone recording happens here</p>
                    </div>
                  </div>
                ) : previewUrl === null && status === 'recording' ? (
                  <video ref={liveVideoRef} autoPlay muted playsInline className="h-[320px] w-full object-cover" />
                ) : previewUrl ? (
                  <video controls playsInline src={previewUrl} className="h-[320px] w-full object-cover" />
                ) : (
                  <div className="grid min-h-[320px] place-items-center px-6 py-10 text-center text-white">
                    <div>
                      <Video className="mx-auto h-12 w-12 text-orange-300" />
                      <p className="mt-4 text-base font-semibold">Camera preview appears here</p>
                    </div>
                  </div>
                )}
              </div>

              <canvas ref={canvasRef} width={420} height={120} className="mt-4 h-28 w-full rounded-2xl border border-orange-100 bg-white" />

              <div className="mt-5 flex flex-wrap gap-3">
                <button type="button" onClick={handleStartRecording} disabled={status === 'recording' || status === 'uploading'} className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60">
                  <ActiveIcon className="h-4 w-4" />
                  Start recording
                </button>
                <button type="button" onClick={() => recorderRef.current?.state === 'recording' && recorderRef.current.stop()} disabled={status !== 'recording'} className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-50">
                  <Square className="h-4 w-4" />
                  Stop
                </button>
                <button type="button" onClick={() => resetTake(`Ready to capture ${mode}.`)} className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
                  <RefreshCcw className="h-4 w-4" />
                  Reset
                </button>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
                  <UploadCloud className="h-4 w-4" />
                  Choose file
                  <input type="file" accept={config.accept} onChange={handlePickedFile} className="hidden" />
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Name and upload</h2>
              <label className="mt-4 grid gap-2 text-sm">
                <span className="font-semibold text-slate-700">Recording name</span>
                <input value={recordingName} onChange={(event) => setRecordingName(event.target.value)} className="rounded-2xl border border-orange-200 px-4 py-3 focus:border-orange-400 focus:outline-none" />
              </label>
              <div className="mt-4 rounded-2xl border border-orange-100 bg-orange-50/70 p-4 text-xs text-slate-600">
                Current endpoint: <span className="font-mono text-orange-700">POST /media/recordings/{config.endpoint}</span>
              </div>
              {previewUrl && mode === 'audio' ? <audio controls src={previewUrl} className="mt-4 w-full" /> : null}
              <button type="button" onClick={handleUpload} disabled={!recordedFile || status === 'recording' || status === 'uploading'} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60">
                <UploadCloud className="h-4 w-4" />
                {status === 'uploading' ? 'Uploading...' : `Upload ${config.label}`}
              </button>
              {uploadResult ? (
                <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-xs text-slate-700">
                  <p><span className="font-semibold text-slate-900">File:</span> {uploadResult.originalName || recordedFile?.name}</p>
                  <p><span className="font-semibold text-slate-900">Size:</span> {formatBytes(uploadResult.size ?? recordedFile?.size)}</p>
                  {uploadResult.url ? <a href={uploadResult.url} target="_blank" rel="noreferrer" className="mt-2 block break-all font-medium text-emerald-700 underline">{uploadResult.url}</a> : null}
                </div>
              ) : null}
              {!supportsRecording ? <p className="mt-3 text-xs font-medium text-red-600">This browser does not expose the MediaRecorder APIs needed for in-app capture.</p> : null}
            </div>

            <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Media endpoints</h2>
              <div className="mt-4 space-y-2">
                {endpointList.map((endpoint) => (
                  <div key={endpoint} className="rounded-2xl border border-orange-100 bg-orange-50/70 p-3 font-mono text-xs text-slate-800">
                    {endpoint}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorAddProductPage;
